import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Users, Plus, Upload, Settings, LogOut, BookOpen, 
  ArrowRight, User, Sparkles, Filter, ChevronDown, BarChart3,
  UserPlus, Building2, RefreshCw, X, MessageSquare, Home
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCommunity } from '../contexts/CommunityContext';

export default function CommunityDashboard() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const {
    community, residents, stats, loading, error, isAdmin, communityId,
    searchQuery, setSearchQuery,
    statusFilter, setStatusFilter,
    careLevelFilter, setCareLevelFilter,
    loadDashboard, addResident
  } = useCommunity();

  // Add resident modal state
  const [showAddResident, setShowAddResident] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPreferredName, setNewPreferredName] = useState('');
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [newCareLevel, setNewCareLevel] = useState('');
  const [addingResident, setAddingResident] = useState(false);
  const [addError, setAddError] = useState(null);

  const handleAddResident = async (e) => {
    e.preventDefault();
    if (!newFirstName.trim() || !newLastName.trim()) {
      setAddError('First and last name are required');
      return;
    }

    setAddingResident(true);
    setAddError(null);

    try {
      const result = await addResident({
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        preferredName: newPreferredName.trim() || null,
        roomNumber: newRoomNumber.trim() || null,
        careLevel: newCareLevel || null
      });

      // Navigate to the new resident's detail page
      if (result.storyId) {
        navigate(`/community/${communityId}/resident/${result.storyId}`);
      }

      setShowAddResident(false);
      resetAddForm();
    } catch (err) {
      setAddError(err.message || 'Failed to add resident');
    } finally {
      setAddingResident(false);
    }
  };

  const resetAddForm = () => {
    setNewFirstName('');
    setNewLastName('');
    setNewPreferredName('');
    setNewRoomNumber('');
    setNewCareLevel('');
    setAddError(null);
  };

  const getCompletionColor = (pct) => {
    if (pct >= 80) return 'bg-green-500';
    if (pct >= 40) return 'bg-yellow-500';
    if (pct > 0) return 'bg-orange-400';
    return 'bg-gray-200';
  };

  const getStatusBadge = (resident) => {
    const pct = resident.story?.completionPercentage || 0;
    const status = resident.story?.status || 'not_started';

    if (pct >= 100) {
      return <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Complete</span>;
    }
    if (status === 'not_started') {
      return <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">Not Started</span>;
    }
    return <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">{pct}%</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading community...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadDashboard} className="text-quiltt-purple hover:underline">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-quiltt-purple rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{community?.name || 'Community'}</h1>
                <p className="text-sm text-gray-500">{stats.totalResidents} resident{stats.totalResidents !== 1 ? 's' : ''}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </button>
              <button
                onClick={loadDashboard}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate(`/community/${communityId}/questions`)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Manage Questions"
                >
                  <MessageSquare className="w-5 h-5" />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => navigate(`/community/${communityId}/settings`)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={signOut}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-gray-900">{stats.totalResidents}</p>
            <p className="text-sm text-gray-500">Total Residents</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-green-600">{stats.complete}</p>
            <p className="text-sm text-gray-500">Stories Complete</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            <p className="text-sm text-gray-500">In Progress</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-purple-600">{stats.averageCompletion}%</p>
            <p className="text-sm text-gray-500">Avg. Completion</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search residents by name or room..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none bg-white"
            />
          </div>

          {/* Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none px-4 py-2.5 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Care Level Filter */}
          {community?.careLevels?.length > 0 && (
            <div className="relative">
              <select
                value={careLevelFilter}
                onChange={(e) => setCareLevelFilter(e.target.value)}
                className="appearance-none px-4 py-2.5 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none cursor-pointer"
              >
                <option value="all">All Levels</option>
                <option value="unassigned">Unassigned</option>
                {community.careLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}

          {/* Action buttons */}
          <button
            onClick={() => setShowAddResident(true)}
            className="flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Resident
          </button>

          {isAdmin && (
            <button
              onClick={() => navigate(`/community/${communityId}/import`)}
              className="flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
          )}
        </div>

        {/* Residents Grid */}
        {residents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            {stats.totalResidents === 0 ? (
              <>
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No residents yet</h3>
                <p className="text-gray-500 mb-6">Add residents individually or import your roster from a spreadsheet.</p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowAddResident(true)}
                    className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Resident
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => navigate(`/community/${communityId}/import`)}
                      className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-5 rounded-lg transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Import from CSV
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching residents</h3>
                <p className="text-gray-500">Try adjusting your search or filter.</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {residents.map(resident => (
              <div
                key={resident.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer group"
                onClick={() => navigate(`/community/${communityId}/resident/${resident.storyId}`)}
              >
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-quiltt-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {resident.story?.profilePhotoUrl ? (
                        <img 
                          src={resident.story.profilePhotoUrl} 
                          alt={resident.fullName}
                          className="w-12 h-12 rounded-full object-cover" 
                        />
                      ) : (
                        <User className="w-6 h-6 text-quiltt-purple" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {resident.story?.preferredName || resident.preferredName || resident.fullName}
                      </h3>
                      {(resident.preferredName || resident.story?.preferredName) && (
                        <p className="text-sm text-gray-500 truncate">{resident.fullName}</p>
                      )}
                      {resident.roomNumber && (
                        <p className="text-xs text-gray-400">Room {resident.roomNumber}</p>
                      )}
                      {resident.careLevel && (
                        <span className="inline-block text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded mt-0.5">
                          {resident.careLevel}
                        </span>
                      )}
                    </div>

                    {getStatusBadge(resident)}
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getCompletionColor(resident.story?.completionPercentage || 0)}`}
                        style={{ width: `${Math.max(2, resident.story?.completionPercentage || 0)}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {resident.story?.hasSummaries && (
                        <span className="flex items-center gap-1 text-xs text-purple-600" title="AI Insights Available">
                          <Sparkles className="w-3.5 h-3.5" />
                          Insights
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {resident.story?.answeredCount || 0} questions
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-quiltt-purple transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Resident Modal */}
      {showAddResident && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Add Resident</h2>
              <button
                onClick={() => { setShowAddResident(false); resetAddForm(); }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {addError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {addError}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    placeholder="First name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    placeholder="Last name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name</label>
                <input
                  type="text"
                  value={newPreferredName}
                  onChange={(e) => setNewPreferredName(e.target.value)}
                  placeholder="What they like to be called"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Number</label>
                <input
                  type="text"
                  value={newRoomNumber}
                  onChange={(e) => setNewRoomNumber(e.target.value)}
                  placeholder="e.g., 204A"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                />
              </div>

              {community?.careLevels?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Care Level</label>
                  <select
                    value={newCareLevel}
                    onChange={(e) => setNewCareLevel(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Select care level...</option>
                    {community.careLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowAddResident(false); resetAddForm(); }}
                  className="flex-1 px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddResident}
                  disabled={addingResident || !newFirstName.trim() || !newLastName.trim()}
                  className="flex-1 flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  {addingResident ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add & Start Story
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
